import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Pagination,
} from "@mui/material";
import {
  useGetFieldActivitiesQuery,
  useCreateFieldActivityMutation,
  useUpdateFieldActivityMutation,
  useDeleteFieldActivityMutation,
  FieldActivity,
} from "../../../entities/admin-fieldactivities";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import React from "react";

// Interface for FieldActivityCard props
interface FieldActivityCardProps {
  fieldActivity: FieldActivity;
  onEdit: (fieldActivity: FieldActivity) => void;
  onDelete: (fieldActivity: FieldActivity) => void;
}

// FieldActivityCard component for displaying each field activity
const FieldActivityCard = ({
  fieldActivity,
  onEdit,
  onDelete,
}: FieldActivityCardProps) => {
  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <CardContent sx={{ flexGrow: 1, py: 2 }}>
        <Typography>
          {fieldActivity.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {fieldActivity.description || "Описание отсутствует"}
        </Typography>
      </CardContent>

      <Box sx={{ display: "flex", pr: 2 }}>
        <IconButton size="small" onClick={() => onEdit(fieldActivity)}>
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(fieldActivity)}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Card>
  );
};

export const FieldActivitiesManagement = () => {
  const [selectedFieldActivity, setSelectedFieldActivity] =
    useState<FieldActivity | null>(null);

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Form states
  const [fieldActivityName, setFieldActivityName] = useState("");
  const [fieldActivityDescription, setFieldActivityDescription] = useState("");

  // Queries and mutations
  const { data: fieldActivitiesData, isLoading: isLoadingFieldActivities } =
    useGetFieldActivitiesQuery();

  // Client-side filtering and sorting logic
  const filteredAndSortedActivities = React.useMemo(() => {
    if (!fieldActivitiesData?.fieldActivities) return [];

    // Filter by search term
    let filtered = [...fieldActivitiesData.fieldActivities];
    if (searchTerm.trim()) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by field
    filtered.sort((a, b) => {
      const valueA = a[sortField as keyof FieldActivity] || "";
      const valueB = b[sortField as keyof FieldActivity] || "";

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortOrder === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return 0;
    });

    return filtered;
  }, [fieldActivitiesData, searchTerm, sortField, sortOrder]);

  // Pagination
  const paginatedActivities = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return filteredAndSortedActivities.slice(startIdx, endIdx);
  }, [filteredAndSortedActivities, currentPage, pageSize]);

  useEffect(() => {
    // Calculate total pages
    if (filteredAndSortedActivities) {
      setTotalPages(Math.ceil(filteredAndSortedActivities.length / pageSize));
    }
  }, [filteredAndSortedActivities, pageSize]);

  // Queries and mutations
  const [createFieldActivity, { isLoading: isCreating }] =
    useCreateFieldActivityMutation();
  const [updateFieldActivity, { isLoading: isUpdating }] =
    useUpdateFieldActivityMutation();
  const [deleteFieldActivity, { isLoading: isDeleting }] =
    useDeleteFieldActivityMutation();

  // Handlers
  const handleOpenCreateDialog = () => {
    setFieldActivityName("");
    setFieldActivityDescription("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (fieldActivity: FieldActivity) => {
    setSelectedFieldActivity(fieldActivity);
    setFieldActivityName(fieldActivity.name);
    setFieldActivityDescription(fieldActivity.description || "");
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (fieldActivity: FieldActivity) => {
    setSelectedFieldActivity(fieldActivity);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const isFormValid = () => {
    return fieldActivityName.trim() !== "";
  };

  const handleCreateFieldActivity = async () => {
    if (!isFormValid()) return;

    try {
      await createFieldActivity({
        name: fieldActivityName.trim(),
        description: fieldActivityDescription.trim(),
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating field activity:", error);
    }
  };

  const handleUpdateFieldActivity = async () => {
    if (!selectedFieldActivity || !isFormValid()) return;

    try {
      await updateFieldActivity({
        id: selectedFieldActivity.id,
        body: {
          name: fieldActivityName.trim(),
          description: fieldActivityDescription.trim(),
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating field activity:", error);
    }
  };

  const handleDeleteFieldActivity = async () => {
    if (!selectedFieldActivity) return;

    try {
      await deleteFieldActivity(selectedFieldActivity.id).unwrap();
      handleCloseDialogs();
      setSelectedFieldActivity(null);
    } catch (error) {
      console.error("Error deleting field activity:", error);
    }
  };

  // Render field activities in a grid
  const renderFieldActivityCards = () => {
    if (!paginatedActivities?.length) {
      return (
        <Typography color="text.secondary" align="center" py={3}>
          Сферы деятельности не найдены
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {paginatedActivities.map((fieldActivity) => (
          <Box key={fieldActivity.id} sx={{ width: "100%" }}>
            <FieldActivityCard
              fieldActivity={fieldActivity}
              onEdit={handleOpenEditDialog}
              onDelete={handleOpenDeleteDialog}
            />
          </Box>
        ))}
      </Box>
    );
  };

  // Render form fields
  const renderFieldActivityForm = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <TextField
        sx={{ mt: 1 }}
        label="Название"
        fullWidth
        value={fieldActivityName}
        onChange={(e) => setFieldActivityName(e.target.value)}
      />
      <TextField
        label="Описание"
        fullWidth
        multiline
        rows={4}
        value={fieldActivityDescription}
        onChange={(e) => setFieldActivityDescription(e.target.value)}
      />
    </Box>
  );

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={handleCloseDialogs}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle textTransform="none" textAlign="center">
        Добавить сферу деятельности
      </DialogTitle>
      <DialogContent>{renderFieldActivityForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateFieldActivity}
          disabled={!isFormValid() || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={openEditDialog}
      onClose={handleCloseDialogs}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Редактировать сферу деятельности</DialogTitle>
      <DialogContent>{renderFieldActivityForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateFieldActivity}
          disabled={!isFormValid() || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box m={2}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление сферами деятельности
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 1, height: "12vh" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
            gap: 12,
            alignItems: "space-between",
            justifyContent: "space-between",
          }}
        >
          <TextField
            fullWidth
            label="Поиск сфер деятельности"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Сортировка</InputLabel>
            <Select
              value={sortField}
              label="Сортировка"
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <MenuItem value="name">Название</MenuItem>
              <MenuItem value="createdAt">Дата создания</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, height: "72vh" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography gutterBottom fontWeight={600} fontSize={18}>
            Сферы деятельности
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Добавить
          </Button>
        </Box>

        {isLoadingFieldActivities ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                height: "60vh",
                overflowY: "auto",
                mb: 2,
              }}
            >
              {renderFieldActivityCards()}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {renderCreateDialog()}
      {renderEditDialog()}
      {openDeleteDialog && selectedFieldActivity && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
          <DialogTitle>Удалить сферу деятельности</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить сферу деятельности "
              {selectedFieldActivity.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Отмена</Button>
            <Button
              color="error"
              onClick={handleDeleteFieldActivity}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
